
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';

export interface UserProfile {
  id: string;
  prenom: string | null;
  nom: string | null;
  email: string | null;
  adresse: string | null;
  telephone: string | null;
  photo_profil_url: string | null;
  date_abonnement: string | null;
  created_at: string;
  updated_at: string;
}

export const useUserProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchProfile = async (attempt = 0): Promise<void> => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profils_utilisateur')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Erreur lors du chargement du profil:', error);
        // Retry silencieux sur erreur réseau transitoire (Safari annule souvent les fetch)
        if (attempt < 2) {
          setLoading(false);
          await new Promise(r => setTimeout(r, 600));
          return fetchProfile(attempt + 1);
        }
        // Pas de toast bruyant — on garde l'éventuel profil déjà chargé
      } else {
        setProfile(data || null);
      }
    } catch (err) {
      console.error('Erreur:', err);
      if (attempt < 2) {
        setLoading(false);
        await new Promise(r => setTimeout(r, 600));
        return fetchProfile(attempt + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData: Partial<UserProfile>) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { data, error } = await supabase
        .from('profils_utilisateur')
        .upsert([{ 
          id: user.id,
          ...profileData,
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la mise à jour du profil:', error);
        toast({
          title: "Erreur",
          description: "Impossible de mettre à jour votre profil.",
          variant: "destructive",
        });
        return { error };
      } else {
        setProfile(data);
        toast({
          title: "Succès",
          description: "Profil mis à jour avec succès !",
        });
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const uploadProfilePhoto = async (file: File) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/profile.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(fileName, file, {
          upsert: true
        });

      if (uploadError) {
        console.error('Erreur upload:', uploadError);
        toast({
          title: "Erreur",
          description: "Impossible de télécharger la photo.",
          variant: "destructive",
        });
        return { error: uploadError };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(fileName);

      await updateProfile({ photo_profil_url: publicUrl });
      
      return { data: publicUrl };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  return {
    profile,
    loading,
    updateProfile,
    uploadProfilePhoto,
    refetch: fetchProfile
  };
};
